import { createFileRoute } from "@tanstack/react-router";
import { Sparkles } from "lucide-react";

export const Route = createFileRoute("/ai-disclaimer")({
  head: () => ({
    meta: [
      { title: "AI Disclaimer — Recovery Trauma™" },
      {
        name: "description",
        content:
          "How Recovery Trauma™ uses AI for reflection prompts, and the limits of those responses.",
      },
      { property: "og:title", content: "AI Disclaimer — Recovery Trauma™" },
      {
        property: "og:description",
        content:
          "Recovery Trauma™ AI reflections are educational, not therapy or medical advice.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://recoverytrauma.com/ai-disclaimer" },
    ],
    links: [
      { rel: "canonical", href: "https://recoverytrauma.com/ai-disclaimer" },
    ],
  }),
  component: AiDisclaimerPage,
});

function AiDisclaimerPage() {
  return (
    <div className="mx-auto max-w-3xl px-4 py-10">
      <header className="mb-8 text-center">
        <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs text-muted-foreground">
          <Sparkles className="h-3.5 w-3.5" /> AI Reflections
        </div>
        <h1 className="font-serif text-4xl tracking-tight text-foreground md:text-5xl">
          AI Disclaimer
        </h1>
      </header>

      <div className="space-y-5 rounded-2xl border border-border bg-card p-6 text-sm leading-relaxed text-foreground/85">
        <p>
          Some Recovery Trauma™ features use artificial intelligence to offer
          gentle, reflective prompts based on the words you share — for
          example, journal and dream journal reflections.
        </p>
        <ul className="list-disc space-y-2 ps-5">
          <li>
            <strong>Responses are automated.</strong> They are generated by
            software, not a human therapist.
          </li>
          <li>
            <strong>Reflections are educational only.</strong> They are not
            therapy, diagnosis, medical advice, or crisis intervention.
          </li>
          <li>
            <strong>Reflections are exploratory.</strong> They may use phrases
            like <em>"One possible reflection…"</em> or <em>"You might
            notice…"</em>. They are not interpretations of what your
            experiences mean.
          </li>
          <li>
            <strong>Your content stays private.</strong> Inputs are processed
            to generate a reflection and are not used to identify you publicly.
          </li>
          <li>
            <strong>Always defer to a qualified professional</strong> for
            medical, psychological, or legal advice. If you or someone else is
            in immediate danger, contact your local emergency service (999 in
            the UK, 911 in the US). For emotional support, call Samaritans on
            116 123 (UK and Ireland) or call or text 988 (US).
          </li>
        </ul>

        <div className="rounded-xl border border-amber-200 bg-amber-50/70 p-4 text-xs leading-relaxed text-foreground/85">
          <p className="font-semibold text-amber-900">
            AI Reflection Disclaimer
          </p>
          <p className="mt-1">
            AI-generated reflections are educational and reflective tools only.
            They are not therapy, diagnosis, treatment, crisis support, or
            professional advice.
          </p>
        </div>

        <p className="text-xs text-muted-foreground">
          This document is provided as a template and should be reviewed by
          your legal counsel before launch.
        </p>
      </div>
    </div>
  );
}
